import isEmpty from "lodash/isEmpty";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import {
  LoginAnalyticsEvent,
  LoginAnalyticsPageView,
} from "@/constants/analytics/Login";
import { LoginTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useAuth from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import { useLoginDispatch, useLoginSelector } from "@/store/reducers/login";
import { Button, FAIconName, IconButton, TextField } from "@/ui/core";
import { HStack, KeyboardAvoidingView, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

const ForgotPasswordForm = () => {
  const { isLoading, resetPassword } = useAuth();
  const { track } = useAnalytics();

  const { showToast } = useToast();
  const { email } = useLoginSelector();
  const { setEmail } = useLoginDispatch();

  const isButtonDisabled = isEmpty(email);

  const handleBackNavigation = () => {
    track(LoginAnalyticsEvent.BackToLoginPressed, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.ForgotPassword,
    });
    router.back();
  };

  const handleResetPassword = async () => {
    track(LoginAnalyticsEvent.SendResetEmailPressed, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.ForgotPassword,
    });
    const response = await resetPassword(email);
    if (response.error) {
      showToast({
        text: "Failed to send reset password email",
        variant: "error",
      });
      return;
    }
    router.back();
    showToast({ text: "Reset password email sent", variant: "success" });
  };

  return (
    <KeyboardAvoidingView>
      <VStack gap={24}>
        <TextField
          autoComplete="email"
          isBorderless
          onChange={(value) => setEmail(value)}
          placeholder="Email address"
          testID={joinTestIDs(LoginTestID.Login, LoginTestID.Email)}
          value={email}
        />
        <HStack gap={24}>
          <IconButton
            iconName={FAIconName.ArrowLeft}
            onPress={handleBackNavigation}
            shouldShowBorder
            testID={joinTestIDs(LoginTestID.ForgotPassword, "back")}
          />
          <Button
            isDisabled={isButtonDisabled}
            isFullWidth
            isLoading={isLoading}
            leftIcon={FAIconName.PaperPlane}
            onPress={handleResetPassword}
            testID={joinTestIDs(LoginTestID.ForgotPassword, "reset")}
            title="Send reset link"
            variant="primary.on_background"
          />
        </HStack>
      </VStack>
    </KeyboardAvoidingView>
  );
};

export default ForgotPasswordForm;
